import type {
  DocFolderWithAccess,
  DocPage,
  DocPageWithContent,
} from "@estelle/domain";

export interface FolderNode {
  folder: DocFolderWithAccess;
  children: FolderNode[];
}

/**
 * Folders nested by `parentId`, sorted by path at every level.
 *
 * A folder whose parent did not come back from `listFolders` is rendered as
 * a root of its own rather than dropped: FR-DOC-8's inheritance lets a
 * folder's own grant reach further than its parent's does, so a visible
 * folder can have an invisible parent, and an absent parent is not a reason
 * to hide something someone can otherwise read.
 */
export function buildFolderTree(
  folders: readonly DocFolderWithAccess[],
): FolderNode[] {
  const byId = new Map(folders.map((folder) => [folder.id, folder]));
  const childrenOf = new Map<string, DocFolderWithAccess[]>();
  const roots: DocFolderWithAccess[] = [];

  for (const folder of folders) {
    const parent = folder.parentId ? byId.get(folder.parentId) : undefined;
    if (parent) {
      const siblings = childrenOf.get(parent.id) ?? [];
      siblings.push(folder);
      childrenOf.set(parent.id, siblings);
    } else {
      roots.push(folder);
    }
  }

  function byPath(a: DocFolderWithAccess, b: DocFolderWithAccess): number {
    return a.path.localeCompare(b.path);
  }

  function toNode(folder: DocFolderWithAccess): FolderNode {
    const children = (childrenOf.get(folder.id) ?? []).sort(byPath);
    return { folder, children: children.map(toNode) };
  }

  return roots.sort(byPath).map(toNode);
}

/** The first page in path order, for a default selection with nothing else to go on. */
export function firstPageId(
  folders: readonly DocFolderWithAccess[],
  pagesByFolder: ReadonlyMap<string, { id: string }[]>,
): string | null {
  const sorted = [...folders].sort((a, b) => a.path.localeCompare(b.path));
  for (const folder of sorted) {
    const first = pagesByFolder.get(folder.id)?.[0];
    if (first) return first.id;
  }
  return null;
}

/**
 * `pagesByFolder`, narrowed to pages whose title matches `term`.
 *
 * A folder with no match is dropped from the map entirely rather than kept
 * with an empty array — `foldersToShow` below reads "has an entry" as "has a
 * match somewhere in it", and an empty array would say the opposite of what
 * it means.
 *
 * By title only, not a folder's own name: `DocPage` calls the field `title`,
 * `DocFolder` calls its own `name`, and the tree's search box promises to
 * filter "by title" — matching folder names too would be a second, undocumented
 * behaviour the box's own label does not mention.
 */
export function filterPagesByTitle(
  pagesByFolder: ReadonlyMap<string, DocPage[]>,
  term: string,
): Map<string, DocPage[]> {
  const needle = term.trim().toLowerCase();
  if (needle === "") return new Map(pagesByFolder);

  const result = new Map<string, DocPage[]>();
  for (const [folderId, pages] of pagesByFolder) {
    const matches = pages.filter((page) =>
      page.title.toLowerCase().includes(needle),
    );
    if (matches.length > 0) result.set(folderId, matches);
  }
  return result;
}

/**
 * `folders`, narrowed to ones that themselves hold a match in
 * `matchingPagesByFolder`, or sit above one that does.
 *
 * Keeping ancestors is what lets a hit three levels down still show up as a
 * path rather than a page floating with no tree around it — the point of a
 * folder tree in the first place.
 */
export function foldersToShow(
  folders: readonly DocFolderWithAccess[],
  matchingPagesByFolder: ReadonlyMap<string, DocPage[]>,
): DocFolderWithAccess[] {
  const byId = new Map(folders.map((folder) => [folder.id, folder]));
  const keep = new Set<string>();

  for (const folderId of matchingPagesByFolder.keys()) {
    let current = byId.get(folderId);
    while (current) {
      keep.add(current.id);
      current = current.parentId ? byId.get(current.parentId) : undefined;
    }
  }

  return folders.filter((folder) => keep.has(folder.id));
}

/**
 * The pages edited most recently, newest first (#641): the "Recent" group at
 * the top of the tree, which replaced Recent as a sidebar entry. Ordered by
 * the current revision's time; a page whose content did not load is left out.
 */
export function recentPages(
  pages: readonly DocPage[],
  contentByPageId: ReadonlyMap<string, DocPageWithContent>,
  count: number,
): DocPage[] {
  return pages
    .filter((page) => contentByPageId.has(page.id))
    .sort((a, b) => {
      const at = (page: DocPage) =>
        contentByPageId.get(page.id)?.revision.createdAt ?? "";
      return at(b).localeCompare(at(a));
    })
    .slice(0, count);
}

/**
 * A folder and its parents by name, outermost first — "Finance / GST" — for
 * the muted line above a page's title (#641), in place of its "/finance/gst"
 * path. A parent the caller cannot read is left out, as the tree leaves it.
 */
export function folderTrail(
  folders: readonly DocFolderWithAccess[],
  folderId: string,
): string {
  const byId = new Map(folders.map((folder) => [folder.id, folder]));
  const names: string[] = [];
  const seen = new Set<string>();
  let current = byId.get(folderId);
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    names.unshift(current.name);
    current = current.parentId ? byId.get(current.parentId) : undefined;
  }
  return names.join(" / ");
}
